import { qcCellRenderer } from '@/components/UI/QCBadge/qcCellRenderer';
import { studyQueryKeys } from '@/config/queryKeys';
import { smIconSize } from '@/constants';
import { useDateFormatter, useStudySamplesCsvDownload } from '@/hooks';
import { createAgGridDatasource, parseFilterModel } from '@/lib/createAgGridDatasource';
import { searchStudySamples } from '@/services/samples';
import {
  PaginatedSamplesResponse,
  QC_OUTCOME_TYPES,
  SampleFilters,
  SampleSearchRequest,
  SampleSortField,
  SPECIES_VALUES,
  speciesLabel,
  StudySample,
} from '@/types';
import { flushColumnState } from '@/utils/flushColumnState';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { CloudDownload } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSampleTableGrid } from '../../Studies/components/useSampleTableGrid';
import { createSetFilterParams, DATE_FILTER_PARAMS, TEXT_FILTER_PARAMS } from '../../Studies/utils/sharedColumnDefs';
import styles from '../styles.module.css';

interface StudySamplesTableProps {
  orgId: string;
  studyId: string;
  totalSamples: number;
  passedQc: number;
}

const paginationPageSizeKey = (studyId: string) => `study-${studyId}-studySamples-pageSize`;
const VALID_SORT_FIELDS = ['name', 'instrument_run_name', 'created_at', 'updated_at', 'well_position', 'demux_index'];
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 1000];
const DEFAULT_PAGE_SIZE = 25;

const gridOptions: GridOptions = {
  cacheBlockSize: 100,
  maxBlocksInCache: 10,
  suppressMovableColumns: false,
  suppressDragLeaveHidesColumns: false,
};

export const StudySamplesTable = function StudySamplesTable({
  orgId,
  passedQc,
  studyId,
  totalSamples,
}: StudySamplesTableProps) {
  const { t } = useTranslation('studies');
  const formatDate = useDateFormatter();
  const gridRef = useRef<AgGridReact<StudySample>>(null);
  const downloadSamples = useStudySamplesCsvDownload(orgId, studyId);

  const datasource = useMemo(
    () =>
      createAgGridDatasource<StudySample, PaginatedSamplesResponse, SampleSearchRequest>(
        studyQueryKeys.endpoints.samplesSearch(orgId, studyId),
        ({ filters, limit, skip, sortBy, sortOrder }) => {
          const validSortBy = sortBy && VALID_SORT_FIELDS.includes(sortBy) ? sortBy : 'created_at';

          return {
            skip,
            limit,
            sort_by: validSortBy as
              | 'name'
              | 'instrument_run_name'
              | 'created_at'
              | 'updated_at'
              | 'well_position'
              | 'demux_index'
              | 'study_name'
              | 'org_name',
            sort_order: (sortOrder ?? 'desc') as 'asc' | 'desc',
            filters: Object.keys(filters).length > 0 ? (filters as SampleSearchRequest['filters']) : null,
          };
        },
        {
          fetcher: request => searchStudySamples(orgId, studyId, request),
        },
      ),
    [orgId, studyId],
  );

  const {
    defaultColDef,
    handleFilterChanged,
    handleFirstDataRendered,
    handleGridReady,
    handleModelUpdated,
    handlePaginationChanged,
    pageSize,
  } = useSampleTableGrid<StudySample>({
    studyId,
    tableId: 'study-samples-table',
    validSortFields: VALID_SORT_FIELDS,
    paginationPageSizeKeyFn: paginationPageSizeKey,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    datasource,
    autoHeight: true,
  });

  const colDefs: ColDef<StudySample>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('columnHeaders.sampleName'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'sample_group',
        headerName: t('columnHeaders.sampleGroup'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      // No `status` column by design — see the QcOutcomeType/PRQ-113 note in src/types/index.ts.
      {
        cellRenderer: qcCellRenderer,
        field: 'sample_quality_qc',
        headerName: t('columnHeaders.sampleQualityQc'),
        // Enum container (equals/not_equals): must be a set filter, not the text filter that
        // defaultColDef would otherwise supply — 'contains' is rejected with a 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('sample_quality_qc', [...QC_OUTCOME_TYPES]),
        resizable: true,
        sortable: false,
      },
      {
        cellRenderer: qcCellRenderer,
        field: 'decode_qc',
        headerName: t('columnHeaders.decodeQc'),
        // Enum container (equals/not_equals): must be a set filter, not the text filter that
        // defaultColDef would otherwise supply — 'contains' is rejected with a 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('decode_qc', [...QC_OUTCOME_TYPES]),
        resizable: true,
        sortable: false,
      },
      {
        cellRenderer: qcCellRenderer,
        field: 'lane_qc',
        headerName: t('columnHeaders.laneQc'),
        // Enum container (equals/not_equals): must be a set filter, not the text filter that
        // defaultColDef would otherwise supply — 'contains' is rejected with a 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('lane_qc', [...QC_OUTCOME_TYPES]),
        resizable: true,
        sortable: false,
      },
      {
        cellRenderer: qcCellRenderer,
        field: 'controls_qc',
        headerName: t('columnHeaders.controlsQc'),
        // Enum container (equals/not_equals): must be a set filter, not the text filter that
        // defaultColDef would otherwise supply — 'contains' is rejected with a 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('controls_qc', [...QC_OUTCOME_TYPES]),
        resizable: true,
        sortable: false,
      },
      {
        // Raw, a boolean renders as the literal "false"; see SamplesPage.tsx for the peer.
        // null means no warning recorded, which reads the same as false to a user.
        cellRenderer: ({ value }: { value: boolean | null }) => (
          <Text as="span">{value === true ? t('warning_yes') : t('warning_no')}</Text>
        ),
        field: 'warning',
        headerName: t('columnHeaders.warning'),
        // Filterable on SamplesPage only: that grid's datasource declares `booleanFields`, which
        // this one needs before AG Grid's stringified "true"/"false" would reach the API as bools.
        filter: false,
        resizable: true,
        sortable: false,
      },
      {
        field: 'instrument_run_name',
        headerName: t('columnHeaders.instrumentRun'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: true,
      },
      {
        field: 'assay_friendly_name',
        headerName: t('columnHeaders.assay'),
        filter: 'agTextColumnFilter',
        filterParams: TEXT_FILTER_PARAMS,
        resizable: true,
        sortable: false,
      },
      {
        field: 'species',
        headerName: t('columnHeaders.species'),
        valueFormatter: ({ value }) => (value ? speciesLabel(value) : ''),
        // Enum column (SpeciesFieldFilters): equals/not_equals only, so the text filter
        // defaultColDef would supply is rejected with a 422.
        filter: 'agSetColumnFilter',
        filterParams: createSetFilterParams('species', [...SPECIES_VALUES], speciesLabel),
        resizable: true,
        sortable: false,
      },
      {
        field: 'created_at',
        headerName: t('columnHeaders.created'),
        filter: 'agDateColumnFilter',
        filterParams: DATE_FILTER_PARAMS,
        resizable: true,
        sortable: true,
        valueFormatter: ({ value }) => (value ? formatDate(value) : ''),
      },
      {
        // TODO: backend to add created_by_full_name to SampleWithNamesPublic
        colId: 'created_by',
        headerName: t('columnHeaders.createdBy'),
        resizable: true,
        sortable: false,
        filter: false,
        hide: true,
      },
    ],
    [formatDate, t],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption('serverSideDatasource', datasource);
    }
  }, [datasource]);

  // Mirror the grid's live sort/filter into the export so a filtered view downloads a filtered
  // CSV. Uses the datasource's own parser, not convertAgGridFiltersToApiFilters — the latter
  // whitelists StudyFilters fields and would silently drop every sample-specific column.
  const handleDownload = useCallback(() => {
    const api = gridRef.current?.api;
    const sortedColumn = api?.getColumnState().find(col => col.sort);
    const filters = parseFilterModel(api?.getFilterModel() ?? {});

    downloadSamples.mutate(
      {
        sortBy:
          sortedColumn?.colId && VALID_SORT_FIELDS.includes(sortedColumn.colId)
            ? (sortedColumn.colId as SampleSortField)
            : undefined,
        sortOrder: sortedColumn?.sort ?? undefined,
        filters: Object.keys(filters).length > 0 ? (filters as SampleFilters) : null,
      },
      { onError: () => toast.error(t('study.studySamplesDownloadFailed')) },
    );
  }, [downloadSamples, t]);

  return (
    <Stack className={styles.tableContainer} fullWidth gap={4} orientation="vertical">
      <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
        <Text as="h3">{t('study.studySamplesTitle')}</Text>
        <Stack alignItems="center" gap={4} orientation="horizontal">
          <Text weight="bold">{t('study.studySamplesTotal')}:</Text>
          <Text>{totalSamples}</Text>
          <Text weight="bold">{t('study.studySamplesPassedQc')}:</Text>
          <Text>{passedQc}</Text>
          <Button
            data-testid="study-samples-download"
            isPending={downloadSamples.isPending}
            onPress={handleDownload}
            size="sm"
            visual="unstyled"
          >
            <CloudDownload size={smIconSize} />
            {t('study.studySamplesDownload')}
          </Button>
        </Stack>
      </Stack>

      <Table
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        domLayout="autoHeight"
        enableAdvancedFilter={false}
        enableResponsiveResize={false}
        getRowId={(params: { data: StudySample }) => params.data.id}
        gridOptions={gridOptions}
        onFilterChanged={handleFilterChanged}
        onFirstDataRendered={handleFirstDataRendered}
        onGridPreDestroyed={e => flushColumnState(e.api, 'study-samples-table')}
        onGridReady={handleGridReady}
        onModelUpdated={handleModelUpdated}
        onPaginationChanged={handlePaginationChanged}
        pagination
        paginationPageSize={pageSize}
        paginationPageSizeSelector={PAGE_SIZE_OPTIONS}
        ref={gridRef}
        resizeDebounceMs={150}
        rowModelType="serverSide"
        tableId="study-samples-table"
      />
    </Stack>
  );
};
